import { Clients, Streams } from '.';

function useScalarProperty<StreamTypeParams extends Streams.TypeParams>(
    stream: Streams.ScalarStream<StreamTypeParams>
) {
    const variable = Clients.ScalarClient.Eager<StreamTypeParams['Value']>('property');
    variable.upstream(stream);
    if(!stream.pull) return undefined;
    return stream.pull().then(() => ({
        value: variable.value(),
        domain: stream.domain,
        client: stream.stateful()
    }));
}

function useSetProperty<StreamTypeParams extends Streams.TypeParams>(
    stream: Streams.SetStream<StreamTypeParams>
) {
    const variable = Clients.SetClient.Eager<StreamTypeParams['Value']>('property');
    variable.upstream(stream);
    if(!stream.pull) return undefined;
    return stream.pull().then(() => ({
        value: variable.value(),
        domain: stream.domain,
        client: stream.stateful()
    }));
}

function useSequenceProperty<StreamTypeParams extends Streams.TypeParams>(
    stream: Streams.SequenceStream<StreamTypeParams>
) {
    const variable = Clients.SequenceClient.Eager<StreamTypeParams['Value']>('property');
    variable.upstream(stream);
    if(!stream.pull) return undefined;
    return stream.pull().then(() => ({
        value: variable.value(),
        domain: stream.domain,
        client: stream.stateful()
    }));
}

function useRelation<StreamTypeParams extends Streams.TypeParams>(
    stream: Streams.RelationStream<StreamTypeParams>
) {
    const variable = Clients.RelationClient.Eager<StreamTypeParams['Binding']>('property');
    variable.upstream(stream);
    if(!stream.pull) return undefined;
    return stream.pull().then(() => ({
        entries: variable.entries(),
        client: stream.stateful()
    }));
}

export function useStream<StreamTypeParams extends Streams.TypeParams>(
    stream: Streams.AnyStream<StreamTypeParams>
) {
    return Promise.all([
        !stream.scalar ? undefined : useScalarProperty(stream.scalar),
        !stream.set ? undefined : useSetProperty(stream.set),
        !stream.sequence ? undefined : useSequenceProperty(stream.sequence),
        !stream.relation ? undefined : useRelation(stream.relation)
    ]).then(([ scalar, set, sequence, relation ]) => {
        return {
            scalar, set, sequence, relation
        }
    });
}

export function useStreams<PatternStreams extends { [streamName:string]: Streams.TypeParams }>(
    streams: { [streamName in keyof PatternStreams]: Streams.AnyStream<PatternStreams[streamName]> }
) {
    return Object.keys(streams).reduce<{
        [name in keyof PatternStreams]?: {
            scalar: ReturnType<typeof useScalarProperty<PatternStreams[name]['Value']>>,
            set: ReturnType<typeof useSetProperty<PatternStreams[name]>>,
            sequence: ReturnType<typeof useSequenceProperty<PatternStreams[name]>>,
            relation: ReturnType<typeof useRelation<PatternStreams[name]['Binding']>>,
        }
    }>((result, streamName) => (
        { ...result, [streamName]: useStream(streams[streamName]) }
    ), {});
}
